import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { userApi } from '../../services/api';
import {
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  LockClosedIcon,
  SparklesIcon,
} from 'components/icons';
import { navGroups, secondaryNav, mobileTabs, canAccess, NavItem } from '../../config/navigation';
import Drawer from '../ui/Drawer';
import ErrorState from '../ui/ErrorState';
import { SkeletonBlock } from '../ui/Skeleton';
import toast from 'react-hot-toast';

const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifError, setNotifError] = useState(false);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const userPlan = user?.plan || 'FREE';

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Every 30s
    return () => clearInterval(interval);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // A background poll every 30s genuinely should stay quiet — a toast on each
  // failed tick would be worse than the missing badge. Swallowed on purpose.
  const fetchUnreadCount = async () => {
    try {
      const res = await userApi.getUnreadCount();
      setUnreadCount(res.data.count || 0);
    } catch {
      /* background poll — intentionally silent */
    }
  };

  // User-initiated, so a failure needs to be visible: an empty dropdown was
  // indistinguishable from having no notifications.
  const fetchNotifications = async () => {
    setNotifError(false);
    setLoadingNotifs(true);
    try {
      const res = await userApi.getNotifications();
      setNotifications(res.data.notifications || []);
    } catch {
      setNotifError(true);
    } finally {
      setLoadingNotifs(false);
    }
  };

  const handleBellClick = () => {
    if (!notifOpen) fetchNotifications();
    setNotifOpen(!notifOpen);
  };

  const handleMarkAllRead = async () => {
    try {
      await userApi.markAllNotificationsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      // Leave the badge alone on failure rather than zeroing a count the
      // server still holds.
      toast.error("Couldn't mark notifications as read.");
    }
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isFreePlan = userPlan === 'FREE';

  /**
   * One nav link, shared by the desktop rail and the mobile drawer. Icons are
   * monochrome — color carries active state and plan locks, nothing else.
   */
  const NavLink: React.FC<{ item: NavItem; onNavigate?: () => void }> = ({ item, onNavigate }) => {
    const locked = !canAccess(item, userPlan);
    const active = isActive(item.href);
    return (
      <Link
        to={locked ? '/pricing' : item.href}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        title={locked ? `${item.name} — requires ${item.plan} plan` : undefined}
        className={`group flex items-center gap-x-3 rounded-token px-2 py-2 text-label transition-colors ${
          locked
            ? 'text-content-subtle hover:bg-surface-sunk'
            : active
              ? 'bg-accent-sunk text-accent font-semibold'
              : 'text-content-muted hover:bg-surface-sunk hover:text-content'
        }`}
      >
        <item.icon className="h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="truncate">{item.name}</span>
        {locked && (
          <LockClosedIcon className="ml-auto h-3.5 w-3.5 shrink-0 text-content-subtle" aria-hidden="true" />
        )}
      </Link>
    );
  };

  const UpgradeCard: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
    <Link
      to="/pricing"
      onClick={onNavigate}
      className="block rounded-token-md bg-accent-sunk ring-1 ring-accent/20 p-3 hover:ring-accent/40 transition-shadow"
    >
      <div className="flex items-center gap-2 text-label font-semibold text-accent">
        <SparklesIcon className="h-4 w-4" aria-hidden="true" />
        Upgrade to Pro
      </div>
      <p className="mt-1 text-caption text-content-muted">
        Unlock AI proposals, CRM, and analytics.
      </p>
    </Link>
  );

  /** Grouped rail. Group labels give each item a stage in the workflow. */
  const NavTree: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => (
    <div className="flex flex-col gap-y-5">
      {navGroups.map((group) => (
        <div key={group.label}>
          {/* "Overview" is a single item — a label above it would be noise. */}
          {group.items.length > 1 && (
            <p className="px-2 pb-1.5 text-overline uppercase text-content-subtle">{group.label}</p>
          )}
          <div className="flex flex-col gap-y-0.5">
            {group.items.map((item) => (
              <NavLink key={item.name} item={item} onNavigate={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-surface-sunk">
      {/* Mobile sidebar */}
      <Drawer
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        title="Navigation"
        hideTitle
        side="left"
        width="w-72"
        footer={
          <div className="px-2 py-3 flex flex-col gap-1">
            {isFreePlan && <UpgradeCard onNavigate={() => setSidebarOpen(false)} />}
            {secondaryNav.map((item) => (
              <NavLink key={item.name} item={item} onNavigate={() => setSidebarOpen(false)} />
            ))}
          </div>
        }
      >
        <div className="px-4 pt-3">
          <Link to="/" className="text-xl font-bold gradient-text">Clevor</Link>
        </div>
        <nav className="px-2 py-4" aria-label="Main">
          <NavTree onNavigate={() => setSidebarOpen(false)} />
        </nav>
      </Drawer>

      {/* Desktop layout */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
        <div className="flex grow flex-col gap-y-5 min-h-0 overflow-y-auto overscroll-contain border-r border-border bg-surface px-6">
          <div className="flex h-16 items-center">
            <Link to="/" className="text-2xl font-bold gradient-text">Clevor</Link>
          </div>
          <nav className="flex flex-1 flex-col pb-4" aria-label="Main">
            <div className="-mx-2">
              <NavTree />
            </div>
            <div className="mt-auto -mx-2 flex flex-col gap-2 pt-6">
              {isFreePlan && <UpgradeCard />}
              <div className="flex flex-col gap-0.5">
                {secondaryNav.map((item) => (
                  <NavLink key={item.name} item={item} />
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-border bg-surface px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            aria-label="Open navigation menu"
            className="-m-2.5 p-2.5 text-content lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 justify-end">
            <div className="flex items-center gap-x-4 lg:gap-x-6">

              <div className="relative" ref={notifRef}>
                <button
                  type="button"
                  onClick={handleBellClick}
                  aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                  aria-expanded={notifOpen}
                  className="-m-2.5 p-2.5 text-content-subtle hover:text-content-muted relative"
                >
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs font-bold">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {notifOpen && (
                  <div className="fixed left-4 right-4 top-16 sm:absolute sm:left-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-96 bg-surface rounded-lg shadow-xl ring-1 ring-border z-50 overflow-hidden">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                      <h3 className="font-semibold text-content text-sm">Notifications</h3>
                      <div className="flex items-center gap-3">
                        {unreadCount > 0 && (
                          <button onClick={handleMarkAllRead} className="text-xs text-brand-600 hover:text-brand-700">
                            Mark all read
                          </button>
                        )}
                        {notifications.length > 0 && (
                          <button
                            onClick={async () => {
                              try {
                                await userApi.clearAllNotifications();
                                setNotifications([]);
                                setUnreadCount(0);
                              } catch {
                                // Don't clear locally on failure — an empty
                                // list the server still holds is worse than an
                                // error, because the notifications reappear on
                                // the next poll with no explanation.
                                toast.error("Couldn't clear notifications.");
                              }
                            }}
                            className="text-caption text-danger hover:brightness-90"
                          >
                            Clear all
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="max-h-96 overflow-y-auto">
                      {loadingNotifs && notifications.length === 0 ? (
                        <div className="p-4 space-y-3">
                          <SkeletonBlock className="h-12" />
                          <SkeletonBlock className="h-12" />
                        </div>
                      ) : notifError ? (
                        <ErrorState
                          title="Couldn't load notifications"
                          description=""
                          onRetry={fetchNotifications}
                          retrying={loadingNotifs}
                          compact
                        />
                      ) : notifications.length === 0 ? (
                        <p className="px-4 py-8 text-center text-content-muted text-body">
                          No notifications yet
                        </p>
                      ) : (
                        notifications.slice(0, 15).map((notif: any) => (
                          <div
                            key={notif.id}
                            className={`flex items-start px-4 py-3 hover:bg-surface-sunk border-b border-gray-50 ${
                              !notif.read ? 'bg-brand-50/50' : ''
                            }`}
                          >
                            <Link
                              to={notif.data?.link || '#'}
                              onClick={async () => {
                                setNotifOpen(false);
                                if (!notif.read) {
                                  try {
                                    await userApi.markNotificationRead(notif.id);
                                    setUnreadCount(prev => Math.max(0, prev - 1));
                                    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
                                  } catch {
                                    // We're navigating away, so a toast would
                                    // be lost. Roll the optimistic decrement
                                    // back instead — the next poll corrects it.
                                    setUnreadCount(prev => prev + 1);
                                  }
                                }
                              }}
                              className="flex items-start gap-3 flex-1 min-w-0"
                            >
                              {!notif.read && (
                                <span className="w-2 h-2 rounded-full bg-brand-500 flex-shrink-0 mt-1.5" />
                              )}
                              <div className={`flex-1 min-w-0 ${notif.read ? 'ml-5' : ''}`}>
                                <p className="text-sm font-medium text-content truncate">{notif.title}</p>
                                <p className="text-xs text-content-muted mt-0.5 line-clamp-2">{notif.message}</p>
                                <p className="text-xs text-content-subtle mt-1">
                                  {new Date(notif.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                </p>
                              </div>
                            </Link>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await userApi.deleteNotification(notif.id);
                                  setNotifications(prev => prev.filter(n => n.id !== notif.id));
                                  if (!notif.read) setUnreadCount(prev => Math.max(0, prev - 1));
                                } catch {
                                  toast.error("Couldn't dismiss that notification.");
                                }
                              }}
                              aria-label={`Dismiss notification: ${notif.title}`}
                              className="ml-2 p-1 text-content-subtle hover:text-danger flex-shrink-0 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-danger"
                            >
                              <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="hidden lg:block lg:h-6 lg:w-px lg:bg-border" />

              <div className="flex items-center gap-x-4">
                <span className="hidden lg:flex lg:items-center">
                  <span className="text-sm font-semibold leading-6 text-content" aria-hidden="true">
                    {user?.firstName} {user?.lastName}
                  </span>
                </span>
                <button
                  onClick={() => logout()}
                  className="text-sm text-content-muted hover:text-content"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page content. Bottom padding on mobile clears the tab bar. */}
        <main className="py-8 pb-28 lg:pb-10">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* ============================================ */}
      {/* Mobile tab bar */}
      {/* ============================================ */}
      {/* This ships as an iOS/Android app via Capacitor, and until now every
          navigation on mobile meant opening a hamburger into a 15-item drawer.
          Four tabs for the daily loop; everything else stays in the drawer. */}
      <nav
        aria-label="Primary"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-surface-raised border-t border-border bottom-nav-safe"
      >
        <div className="flex items-stretch justify-around">
          {mobileTabs.map((item) => {
            const locked = !canAccess(item, userPlan);
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={locked ? '/pricing' : item.href}
                aria-current={active ? 'page' : undefined}
                className={`flex flex-1 flex-col items-center justify-center gap-1 pt-2 pb-1 min-h-[52px] transition-colors ${
                  active ? 'text-accent' : 'text-content-subtle hover:text-content'
                }`}
              >
                <item.icon className="h-6 w-6" aria-hidden="true" />
                <span className="text-[0.625rem] font-semibold leading-none">{item.name}</span>
              </Link>
            );
          })}
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open full menu"
            className="flex flex-1 flex-col items-center justify-center gap-1 pt-2 pb-1 min-h-[52px] text-content-subtle hover:text-content transition-colors"
          >
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            <span className="text-[0.625rem] font-semibold leading-none">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default DashboardLayout;
